<template>
  <YzsDragModalPlus
    v-model:visible="dialogVisible"
    :getContainer="getContainer"
    :title="t('systemRequest')"
    :width="800"
  >
    <div style="font-size: 16px; font-weight: bold; color: #303133; margin-bottom: 16px">
      {{ $t("last5Requests") }} ({{ pageName }} {{ userInfo?.nick }} {{ userInfo?.huid }})
    </div>
    <a-table
      :dataSource="tableData"
      :pagination="false"
      :scroll="{ y: 500 }"
      rowKey="traceId"
    >
      <a-table-column dataIndex="url" :title="t('interface')" width="180" />
      <a-table-column dataIndex="method" :title="t('requestType')" width="80" />
      <a-table-column
        dataIndex="status"
        :title="t('requestStatus')"
        width="150"
        :customRender="renderStatus"
      />
      <a-table-column
        dataIndex="traceId"
        :title="t('issueID')"
        :customRender="renderTraceId"
      />
      <a-table-column dataIndex="requestTime" :title="t('requestTime')" width="150" />
    </a-table>
    <template #footBtn>
      <!-- <a-button @click="handleCLose">取消</a-button> -->
      <a-button style="margin-left: 18px" type="primary" @click="copyAllToClipboard">{{
        $t("copy")
      }}</a-button>
    </template>
  </YzsDragModalPlus>
</template>

<script setup lang="ts">
import { ref, onMounted, h } from "vue";
// @ts-ignore
import { Tag, message } from "ant-design-vue";
import dayjs from "dayjs";
import WujieVue from "wujie-vue3";
import { useI18n } from "vue-i18n";
const { bus } = WujieVue;
const { t } = useI18n();

// ----- 辅助函数 -----
function getPageName(url: string): string | null {
  const regex = /#([a-zA-Z0-9-]+)/;
  const match = url.match(regex);
  return match ? match[1] : null;
}

function getContainer() {
  //兼容不适用通用组件的情况
  if (document.querySelector(".porta-renderer-wrapper")) {
    return document.querySelector(".porta-renderer-wrapper");
  }
  if (document.querySelector(".main__container")) {
    return document.querySelector(".main__container");
  }
  return false;
}

function getMenuText(href: string): string[] {
  // @ts-ignore
  const parentList = window.esapp?.menuMap?.parentList || [];
  for (const category of parentList) {
    for (const subMenu of category.subMenu) {
      if (subMenu.href === href) {
        return [category.text, subMenu.title];
      }
    }
  }
  // @ts-ignore
  const menuList = window.esapp?.menuMap?.menuList || [];
  for (const menu of menuList) {
    if (menu.href === href) {
      return [menu.title];
    }
  }
  return [t("workbenches")];
}

function getModalTitle(): string {
  const modalElement = document.querySelector("div.dk-modal.fade.in");
  if (modalElement) {
    const titleElement = modalElement.querySelector(".modal-title");
    return titleElement ? titleElement.textContent || "" : "";
  }
  return "";
}

// ----- 数据 & 方法 -----
const dialogVisible = ref(false);
const userInfo = ref<any>({});
const pageName = ref("");
const currentTime = ref("");
const tableData = ref<any[]>([]);

function handleCLose() {
  dialogVisible.value = false;
}

function openModal() {
  console.log("openModal");
  dialogVisible.value = true;
  currentTime.value = dayjs().format("YYYY-MM-DD HH:mm:ss");
  // 根据当前 location.hash 获取页面名称
  const pName = getPageName(location.hash) || "";
  const menuList = getMenuText(pName);
  const modalTitle = getModalTitle();
  if (modalTitle) {
    menuList.push(modalTitle);
  }
  pageName.value = menuList.join("/");
}

function handleClose() {
  dialogVisible.value = false;
}

function copyAllToClipboard() {
  const basicInfo = `${t("last5Requests")} (${pageName.value} ${userInfo.value?.nick} ${
    userInfo.value?.huid
  })\n`;
  const requestList = tableData.value
    .map(
      (item: any) =>
        `${t("interface")}：${item.url} ${
          item.status >= 300 ? "(" + item.status + ")" : ""
        }\n${t("issueID")}：${item.traceId}`
    )
    .join("\n");
  const fullText = basicInfo + requestList;
  if (!navigator.clipboard) {
    copyFallback(fullText);
    return;
  }
  navigator.clipboard
    .writeText(fullText)
    .then(() => {
      message.success(t("copySuccessful"));
    })
    .catch(() => {
      message.error(t("copyUnsuccessful"));
    });
}

function copyToClipboard(text: string) {
  if (!navigator.clipboard) {
    copyFallback(text);
    return;
  }

  navigator.clipboard
    .writeText(text)
    .then(() => {
      message.success(t("copySuccessful"));
    })
    .catch(() => {
      message.error(t("copyUnsuccessful"));
    });
}

function copyFallback(text: string) {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed"; // Prevent scrolling to bottom
  document.body.appendChild(textarea);
  textarea.select();

  try {
    const successful = document.execCommand("copy");
    if (successful) {
      message.success(t("copySuccessful"));
    } else {
      // message.error("复制失败，请手动复制");
      message.error(`${(t("copyUnsuccessful"), t("pleaseCopyManually"))}`);
    }
  } catch (err) {
    message.error(`${(t("copyUnsuccessful"), t("pleaseCopyManually"))}`);
    console.error("Fallback copy failed:", err);
  } finally {
    document.body.removeChild(textarea);
  }
}

function renderStatus({ text }: { text: number }) {
  let color: string;
  if (text >= 200 && text < 300) {
    color = "green";
  } else if (text >= 400 && text < 500) {
    color = "red";
  } else {
    color = "gold";
  }
  return h(Tag, { color }, text);
}

function renderTraceId({ text }: { text: string }) {
  return h(
    "span",
    {
      style: { color: "#5a9cf8", cursor: "pointer" },
      onClick: () => copyToClipboard(text),
    },
    text
  );
}

// 请求拦截 (模拟实现，详情根据实际情况调整)
function interceptRequests(currentWindow = window) {
  const originalFetch = currentWindow.fetch;
  currentWindow.fetch = async (...args) => {
    const response = await originalFetch(...args);
    // @ts-ignore
    const { method, url } = args[1] || { method: "GET" };
    if (
      (method === "GET" || method === "POST") &&
      url &&
      !url.match(/\.(html|json|js|tpl|css)$/)
    ) {
      const traceId = response.headers.get("X-Trace-Id");
      const requestTime = new Date().toLocaleString();
      addRequestToTable({
        url: url.split("?")[0],
        method,
        status: response.status,
        traceId,
        requestTime,
      });
    }
    return response;
  };

  const originalXHROpen = currentWindow.XMLHttpRequest.prototype.open;
  currentWindow.XMLHttpRequest.prototype.open = function (
    method: string,
    url: string,
    ...rest: any[]
  ) {
    this.addEventListener("load", function () {
      if (
        (method === "GET" || method === "POST") &&
        url &&
        !url.match(/\.(html|json|js|tpl|css)$/)
      ) {
        const traceId = this.getResponseHeader("X-Trace-Id");
        const requestTime = new Date().toLocaleString();
        addRequestToTable({
          url: url.split("?")[0],
          method,
          status: this.status,
          traceId,
          requestTime,
        });
      }
    });
    // @ts-ignore
    originalXHROpen.call(this, method, url, ...rest);
  };
}

function addRequestToTable(request: any) {
  tableData.value.unshift(request);
  if (tableData.value.length > 5) {
    tableData.value = tableData.value.slice(0, 5);
  }
}

onMounted(() => {
  interceptRequests();
  const storedUserInfo = localStorage.getItem("iam-user-info");
  if (storedUserInfo) {
    userInfo.value = JSON.parse(storedUserInfo);
  }
  bus.$on("sub-request", addRequestToTable);
  // 可根据需要调用 openModal() 打开弹窗
});

defineExpose({
  openModal,
});
</script>

<style scoped lang="scss">
/* 如需自定义 antd-vue 组件样式，可在此添加 */

.footer {
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: 10px;
}
</style>
